/**
 * Slot provider for desktop-specific extensions to shared dashboard views.
 *
 * The dashboard surfaces in `@localagent/app-shell` (SettingsView, etc.)
 * are shared between `apps/web` and `apps/desktop`. The desktop app has
 * additional, platform-specific concerns — auto-update via Sparkle, "About"
 * panel showing daemon/bundle versions, sign-out, uninstall — that don't
 * belong on web. The components implementing those concerns live in
 * `apps/desktop/src/renderer/settings/` (because they import
 * `window.electronAPI` via desktop-api), and `@localagent/app-shell` cannot
 * import from them without creating a circular package dependency.
 *
 * This context inverts the dependency: app-shell defines the slot, and
 * `apps/desktop/src/renderer/index.tsx` fills it with the desktop-specific
 * panels by wrapping `<App />` in `<DesktopExtensionsProvider>`. On web,
 * the provider isn't mounted, the context value is `null`, and SettingsView
 * renders nothing extra — no desktop bits leak into the web bundle.
 *
 * Future surfaces (Quick Settings, command palette, status bar, etc.) can
 * add more slots to this same context as the desktop app needs them, with
 * the same null-default-on-web semantics.
 */

import { createContext, useContext, type ReactNode } from 'react';

export interface DesktopExtensions {
  /** Rendered at the bottom of the shared SettingsView when present. */
  settingsExtras?: ReactNode;
}

const DesktopExtensionsContext = createContext<DesktopExtensions | null>(null);

export function DesktopExtensionsProvider({
  extensions,
  children,
}: {
  extensions: DesktopExtensions;
  children: ReactNode;
}) {
  return (
    <DesktopExtensionsContext.Provider value={extensions}>
      {children}
    </DesktopExtensionsContext.Provider>
  );
}

/** Read the desktop extensions slot. Returns `null` when no provider is
 *  mounted (web platform) — callers should treat that as "no extras". */
export function useDesktopExtensions(): DesktopExtensions | null {
  return useContext(DesktopExtensionsContext);
}
